<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import HotelBookingsAPI from 'dashboard/api/hotelBookings';
import HospedagensAPI from 'dashboard/api/hospedagens';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';
import HotelBookingForm from './HotelBookingForm.vue';
import MotorReservaOrcamentoForm from './MotorReservaOrcamentoForm.vue';
import MotorReservaConfirmForm from './MotorReservaConfirmForm.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const bookings = ref([]);
const rooms = ref([]);
const isLoading = ref(false);
const activeSection = ref('list');
const selectedBooking = ref(null);
const selectedFilter = ref('all');
const isSending = ref(false);

const showConfirmForm = ref(false);
const confirmingBooking = ref(null);
const motorReservaQuoteData = ref(null);
const motorReservaEnabled = computed(
  () => motorReservaQuoteData.value?.enabled === true
);

const contactId = computed(() => props.contact?.id);
const contactName = computed(() => props.contact?.name || 'Cliente');

const filteredBookings = computed(() => {
  if (selectedFilter.value === 'all') return bookings.value;
  if (selectedFilter.value === 'budgets') {
    return bookings.value.filter(b => b.status === 'budget');
  }
  if (selectedFilter.value === 'dayuse') {
    return bookings.value.filter(b => b.booking_type === 'day_use');
  }
  return bookings.value.filter(b => b.status !== 'budget');
});

const statusColor = status => {
  const colors = {
    budget: 'bg-n-blue-2 text-n-blue-11',
    confirmed: 'bg-n-teal-2 text-n-teal-11',
    checked_in: 'bg-n-amber-2 text-n-amber-11',
    checked_out: 'bg-n-slate-3 text-n-slate-11',
    cancelled: 'bg-n-ruby-2 text-n-ruby-11',
  };
  return colors[status] || 'bg-n-slate-3 text-n-slate-11';
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const fetchBookings = async () => {
  if (!contactId.value) return;
  isLoading.value = true;
  try {
    const response = await HotelBookingsAPI.getByContact(contactId.value);
    bookings.value = response.data;
  } catch (error) {
    useAlert('Erro ao carregar reservas');
  } finally {
    isLoading.value = false;
  }
};

const fetchRooms = async () => {
  try {
    const response = await HotelBookingsAPI.getRooms();
    rooms.value = response.data;
  } catch (error) {
    useAlert('Erro ao carregar quartos');
  }
};

const fetchMotorReservaQuoteData = async () => {
  try {
    const response = await HotelBookingsAPI.getMotorReservaQuoteData();
    motorReservaQuoteData.value = response.data;
  } catch {
    motorReservaQuoteData.value = { enabled: false };
  }
};

const createBooking = () => {
  selectedBooking.value = null;
  activeSection.value = 'form';
};

const createDayUse = () => {
  selectedBooking.value = null;
  activeSection.value = 'dayuse';
};

const editBooking = booking => {
  selectedBooking.value = booking;
  if (booking.booking_type === 'day_use') {
    activeSection.value = 'dayuse';
  } else {
    activeSection.value = 'form';
  }
};

const onSaved = () => {
  activeSection.value = 'list';
  fetchBookings();
};

const deleteBooking = async booking => {
  if (!confirm(t('HOTEL_BOOKING.DELETE.DESCRIPTION'))) return;
  try {
    await HotelBookingsAPI.delete(booking.id);
    useAlert(t('HOTEL_BOOKING.API.DELETE_SUCCESS'));
    fetchBookings();
  } catch (error) {
    useAlert(t('HOTEL_BOOKING.API.DELETE_ERROR'));
  }
};

const openConfirmForm = booking => {
  confirmingBooking.value = booking;
  showConfirmForm.value = true;
};

const onBookingConfirmed = () => {
  showConfirmForm.value = false;
  confirmingBooking.value = null;
  fetchBookings();
};

const updateStatus = async (booking, newStatus) => {
  try {
    await HotelBookingsAPI.update(booking.id, {
      hotel_booking: { status: newStatus },
    });
    useAlert(t('HOTEL_BOOKING.API.UPDATE_SUCCESS'));
    fetchBookings();
  } catch (error) {
    useAlert(t('HOTEL_BOOKING.API.UPDATE_ERROR'));
  }
};

const buildDefaultMessage = booking => {
  const room = booking.hotel_room;
  return [
    `🏨 *Orçamento de Hospedagem #${booking.id}*${booking.is_holiday ? ' (Feriado)' : ''}`,
    '',
    `📋 *Quarto:* ${room?.name || '-'}`,
    `👤 *Adultos:* ${booking.adults || booking.guests}`,
    booking.children > 0
      ? `👶 *Crianças (${room?.child1_min_age || 0}-${room?.child1_max_age || 5} anos):* ${booking.children}`
      : null,
    booking.children2 > 0
      ? `👦 *Crianças (${room?.child2_min_age || 6}-${room?.child2_max_age || 12} anos):* ${booking.children2}`
      : null,
    room?.free_age_limit > 0
      ? `🍼 *Grátis até:* ${room.free_age_limit} anos`
      : null,
    room?.amenities ? `✅ *Incluso:* ${room.amenities}` : null,
    '',
    `📅 *Check-in:* ${formatDate(booking.check_in)}`,
    `📅 *Check-out:* ${formatDate(booking.check_out)}`,
    `🌙 *Diárias:* ${booking.nights}`,
    '',
    `💰 *Valor Total: ${formatCurrency(booking.total_price)}*`,
    booking.notes ? `\n📝 *Obs:* ${booking.notes}` : null,
  ]
    .filter(Boolean)
    .join('\n');
};

const buildMessageFromTemplate = (template, booking) => {
  const room = booking.hotel_room;

  const criancas1 =
    booking.children > 0
      ? `👶 *Crianças (${room?.child1_min_age || 0}-${room?.child1_max_age || 5} anos):* ${booking.children}\n`
      : '';
  const criancas2 =
    booking.children2 > 0
      ? `👦 *Crianças (${room?.child2_min_age || 6}-${room?.child2_max_age || 12} anos):* ${booking.children2}\n`
      : '';
  const gratisAte =
    room?.free_age_limit > 0
      ? `🍼 *Grátis até:* ${room.free_age_limit} anos\n`
      : '';
  const amenidades = room?.amenities ? `✅ *Incluso:* ${room.amenities}\n` : '';
  const observacoes = booking.notes ? `\n📝 *Obs:* ${booking.notes}` : '';
  const feriado = booking.is_holiday ? ' (Feriado)' : '';

  return template
    .replace(/\{id\}/g, booking.id)
    .replace(/\{quarto\}/g, room?.name || '-')
    .replace(/\{adultos\}/g, booking.adults || booking.guests)
    .replace(/\{criancas1\}/g, criancas1)
    .replace(/\{criancas2\}/g, criancas2)
    .replace(/\{gratis_ate\}/g, gratisAte)
    .replace(/\{amenidades\}/g, amenidades)
    .replace(/\{checkin\}/g, formatDate(booking.check_in))
    .replace(/\{checkout\}/g, formatDate(booking.check_out))
    .replace(/\{diarias\}/g, booking.nights)
    .replace(/\{valor_total\}/g, formatCurrency(booking.total_price))
    .replace(/\{observacoes\}/g, observacoes)
    .replace(/\{feriado\}/g, feriado);
};

const buildMotorReservaMessage = booking => {
  if (!motorReservaQuoteData.value) return buildDefaultMessage(booking);
  const qd = motorReservaQuoteData.value;
  const room =
    qd.rooms?.find(r => r.id === booking.motor_reserva_room_id) || {};
  const rate =
    qd.rates?.find(r => r.id === booking.motor_reserva_rate_id) || {};
  const pensionLabels = {
    room_only: 'Sem pensão',
    breakfast: 'Café da manhã',
    half_board: 'Meia pensão',
    full_board: 'Pensão completa',
    all_inclusive: 'All inclusive',
  };
  const pension = pensionLabels[rate.pension_type] || '';

  const extrasLines = [];
  if (booking.extras_data && qd.extras) {
    qd.extras.forEach(extra => {
      const qty = booking.extras_data[extra.id] || 0;
      if (qty > 0) {
        let extPrice = Number(extra.price) * qty;
        if (extra.price_type === 'per_night') extPrice *= booking.nights;
        else if (extra.price_type === 'per_person')
          extPrice *= Number(booking.adults) + Number(booking.children);
        extrasLines.push(
          `  + ${extra.name} (${qty}x): ${formatCurrency(extPrice)}`
        );
      }
    });
  }
  const extrasText =
    extrasLines.length > 0 ? `\n🎁 *Extras:*\n${extrasLines.join('\n')}` : '';

  return [
    `🏨 *Orçamento de Hospedagem #${booking.id}*${booking.is_holiday ? ' (Feriado)' : ''}`,
    '',
    `📋 *Quarto:* ${room.name || booking.room_display_name || '-'}`,
    `📊 *Tarifa:* ${rate.name || '-'}`,
    pension ? `🍽️ *Pensão:* ${pension}` : null,
    '',
    `👤 *Adultos:* ${booking.adults || booking.guests}`,
    booking.children > 0 ? `👶 *Crianças:* ${booking.children}` : null,
    booking.children2 > 0 ? `👦 *Crianças F2:* ${booking.children2}` : null,
    '',
    `📅 *Check-in:* ${formatDate(booking.check_in)}${qd.check_in_time ? ` às ${qd.check_in_time}` : ''}`,
    `📅 *Check-out:* ${formatDate(booking.check_out)}${qd.check_out_time ? ` às ${qd.check_out_time}` : ''}`,
    `🌙 *Diárias:* ${booking.nights}`,
    '',
    `💰 *Valor Total: ${formatCurrency(booking.total_price)}*`,
    extrasText || null,
    qd.property_name ? `\n🏨 ${qd.property_name}` : null,
    booking.notes ? `\n📝 *Obs:* ${booking.notes}` : null,
  ]
    .filter(Boolean)
    .join('\n');
};

const sendBudgetMessage = async booking => {
  let message;

  if (motorReservaEnabled.value && booking.motor_reserva_room_id) {
    message = buildMotorReservaMessage(booking);
  } else {
    try {
      const { data } = await HospedagensAPI.getBudgetTemplate();
      if (data.budget_message_template) {
        message = buildMessageFromTemplate(
          data.budget_message_template,
          booking
        );
      } else {
        message = buildDefaultMessage(booking);
      }
    } catch {
      message = buildDefaultMessage(booking);
    }
  }

  try {
    await MessageApi.create({
      conversationId: props.conversationId,
      message: message,
      private: false,
    });
    useAlert('Orçamento enviado na conversa!');
  } catch (error) {
    useAlert('Erro ao enviar orçamento.');
  }
};

// === DAY-USE ===
const dayUseDate = ref('');
const selectedPacote = ref('completo');
const numAdultos = ref(1);
const numCriancasMeia = ref(0);
const dayUseNotes = ref('');
const showDayUseConfig = ref(false);
const isSavingDayUse = ref(false);

const dayUseConfig = ref({
  titulo: '🏊‍♂️ Day-use Sábado e Domingo',
  pacotes: [
    {
      nome: 'Café da manhã + Almoço + Sobremesas',
      preco: 150,
      id: 'completo',
    },
    { nome: 'Somente Almoço', preco: 120, id: 'almoco' },
  ],
  criancas_gratis_ate: 4,
  criancas_meia_de: 6,
  criancas_meia_ate: 11,
  horario_entrada: '09h',
  horario_saida: '17h',
  estrutura: [
    '🐄 Ordenha da vaquinha (as crianças amam!)',
    '🎣 Pesca esportiva',
    '🤾🏻‍♂️ Quadra society e salão de jogos (sinuca, futebol de mesa etc)',
    '🌊 Passeio ao Rio Monteiro',
    '🌳 Trilhas ecológicas',
    '🏊‍♂️ 9 piscinas para todas as idades',
    '🎠 Parquinho infantil e brinquedoteca',
    '🍹 2 restaurantes e bar (bebidas à parte)',
    '🚗 Estacionamento e Wi-Fi gratuitos (na área comum)',
  ],
  nome_hotel: "Ecoviver Mestre D'Armas Fazenda Hotel",
  chamada_final:
    'É o passeio perfeito para relaxar, se divertir e viver um dia diferente no campo!',
  formas_pagamento: [
    { tipo: 'PIX', disponivel: true },
    { tipo: 'Cartão', disponivel: true },
    { tipo: 'Dinheiro', disponivel: true },
  ],
  pagamento_antecipado: true,
  pagamento_recepcao: true,
});

const currentPacote = computed(
  () =>
    dayUseConfig.value.pacotes.find(p => p.id === selectedPacote.value) ||
    dayUseConfig.value.pacotes[0]
);

const dayUseTotalAdultos = computed(
  () => numAdultos.value * currentPacote.value.preco
);
const dayUseTotalMeia = computed(
  () => numCriancasMeia.value * (currentPacote.value.preco / 2)
);
const dayUseTotal = computed(
  () => dayUseTotalAdultos.value + dayUseTotalMeia.value
);

// Load day-use data when editing
const loadDayUseForEdit = booking => {
  if (booking) {
    dayUseDate.value = booking.day_use_date || '';
    dayUseNotes.value = booking.notes || '';
    numAdultos.value = booking.adults || 1;
    numCriancasMeia.value = booking.children || 0;
    selectedPacote.value = booking.day_use_package || 'completo';
    if (booking.day_use_details) {
      try {
        const details = JSON.parse(booking.day_use_details);
        if (details.titulo) dayUseConfig.value = details;
      } catch (e) {
        /* ignore parse errors */
      }
    }
  } else {
    dayUseDate.value = '';
    dayUseNotes.value = '';
    numAdultos.value = 1;
    numCriancasMeia.value = 0;
    selectedPacote.value = 'completo';
  }
};

const saveDayUse = async () => {
  if (!dayUseDate.value) {
    useAlert('Selecione a data do Day-Use');
    return;
  }
  if (numAdultos.value <= 0 && numCriancasMeia.value <= 0) {
    useAlert('Informe pelo menos 1 pessoa');
    return;
  }

  // Need a room for the booking - pick first available or create a dummy approach
  // We'll use the first room as placeholder since day-use doesn't need a specific room
  const firstRoom = rooms.value[0];
  if (!firstRoom) {
    useAlert(
      'Cadastre pelo menos um quarto/espaço no Tarifário para usar Day-Use'
    );
    return;
  }

  isSavingDayUse.value = true;
  try {
    const nextDay = new Date(dayUseDate.value + 'T00:00:00');
    nextDay.setDate(nextDay.getDate() + 1);
    const checkOutStr = nextDay.toISOString().split('T')[0];

    const payload = {
      hotel_booking: {
        hotel_room_id: firstRoom.id,
        contact_id: contactId.value,
        conversation_id: props.conversationId,
        check_in: dayUseDate.value,
        check_out: checkOutStr,
        guests: Number(numAdultos.value) + Number(numCriancasMeia.value),
        adults: Number(numAdultos.value),
        children: Number(numCriancasMeia.value),
        children2: 0,
        is_holiday: false,
        total_price: dayUseTotal.value,
        notes: dayUseNotes.value,
        status: 'budget',
        booking_type: 'day_use',
        day_use_date: dayUseDate.value,
        day_use_package: selectedPacote.value,
        day_use_details: JSON.stringify(dayUseConfig.value),
      },
    };

    if (selectedBooking.value?.id) {
      await HotelBookingsAPI.update(selectedBooking.value.id, payload);
      useAlert('Day-Use atualizado!');
    } else {
      await HotelBookingsAPI.create(payload);
      useAlert('Day-Use criado!');
    }
    activeSection.value = 'list';
    fetchBookings();
  } catch (error) {
    /* eslint-disable no-console */
    console.error('[DayUse] error:', error?.response?.data);
    /* eslint-enable no-console */
    useAlert('Erro ao salvar Day-Use');
  } finally {
    isSavingDayUse.value = false;
  }
};

const formatDayUseMessage = booking => {
  const d = booking.day_use_details
    ? JSON.parse(booking.day_use_details)
    : dayUseConfig.value;
  const pacote =
    d.pacotes.find(p => p.id === (booking.day_use_package || 'completo')) ||
    d.pacotes[0];

  let msg = `${d.titulo}\n\n`;

  if (contactName.value) {
    msg += `👤 *Cliente:* ${contactName.value}\n`;
  }
  msg += `📅 *Data:* ${formatDate(booking.day_use_date || booking.check_in)}\n\n`;

  msg += `💰 *VALORES:*\n`;
  d.pacotes.forEach(p => {
    if (p.id === 'completo') {
      msg += `☕🍽️ ${p.nome}: ${formatCurrency(p.preco)} (por pessoa)\n`;
    } else {
      msg += `🍽️ ${p.nome}: ${formatCurrency(p.preco)} (por pessoa)\n`;
    }
  });

  msg += `\n👶 Crianças até ${d.criancas_gratis_ate} anos *não pagam*\n`;
  msg += `👦 Crianças de ${d.criancas_meia_de} a ${d.criancas_meia_ate} anos pagam *meia*\n`;
  msg += `\n⌚ *Horário:* Entrada às ${d.horario_entrada} e saída às ${d.horario_saida}\n`;
  msg += `\n💦 *Valor inclui acesso a toda a nossa estrutura de lazer e descanso:*\n\n`;
  msg += `🌿 *Aproveite o melhor do ${d.nome_hotel}:*\n\n`;

  d.estrutura.forEach(item => {
    msg += `${item}\n`;
  });

  msg += `\n${d.chamada_final}\n`;

  // Resumo
  msg += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  msg += `📊 *SEU ORÇAMENTO:*\n`;
  if (booking.adults > 0) {
    msg += `👨‍👩‍👧‍👦 ${booking.adults} adulto(s) x ${formatCurrency(pacote.preco)} = ${formatCurrency(booking.adults * pacote.preco)}\n`;
  }
  if (booking.children > 0) {
    msg += `👦 ${booking.children} criança(s) meia x ${formatCurrency(pacote.preco / 2)} = ${formatCurrency(booking.children * (pacote.preco / 2))}\n`;
  }
  msg += `\n✅ *TOTAL: ${formatCurrency(booking.total_price)}*\n`;

  // Formas de pagamento
  msg += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  msg += `💳 *FORMAS DE PAGAMENTO:*\n\n`;

  const formasAtivas = d.formas_pagamento.filter(f => f.disponivel);
  formasAtivas.forEach(f => {
    if (f.tipo === 'PIX') msg += `📱 PIX\n`;
    else if (f.tipo === 'Cartão') msg += `💳 Cartão de Crédito/Débito\n`;
    else if (f.tipo === 'Dinheiro') msg += `💵 Dinheiro\n`;
  });

  if (d.pagamento_antecipado && d.pagamento_recepcao) {
    msg += `\n📌 *Opções de pagamento:*\n`;
    msg += `✅ Pagamento total antecipado\n`;
    msg += `✅ Parte antecipada + restante na recepção\n`;
  }

  return msg;
};

const sendDayUseMessage = async booking => {
  isSending.value = true;
  try {
    const message = formatDayUseMessage(booking);
    await MessageApi.create({
      conversationId: props.conversationId,
      message,
      private: false,
    });
    useAlert('Proposta Day-Use enviada!');
  } catch (error) {
    useAlert('Erro ao enviar proposta');
  } finally {
    isSending.value = false;
  }
};

const sendDayUseVoucher = async booking => {
  isSending.value = true;
  try {
    const d = booking.day_use_details
      ? JSON.parse(booking.day_use_details)
      : dayUseConfig.value;
    const pacote =
      d.pacotes.find(p => p.id === (booking.day_use_package || 'completo')) ||
      d.pacotes[0];
    const dataFormatada = formatDate(booking.day_use_date || booking.check_in);

    let voucher = `🎫 ═══════════════════\n`;
    voucher += `     *VOUCHER DAY-USE*\n`;
    voucher += `     *#${booking.id}*\n`;
    voucher += `═══════════════════\n\n`;
    voucher += `🏨 *${d.nome_hotel}*\n\n`;
    voucher += `👤 *Nome:* ${contactName.value}\n`;
    voucher += `📅 *Data:* ${dataFormatada}\n`;
    voucher += `📋 *Pacote:* ${pacote.nome}\n\n`;
    voucher += `👥 *Participantes:*\n`;
    if (booking.adults > 0) {
      voucher += `   ${booking.adults} adulto(s)\n`;
    }
    if (booking.children > 0) {
      voucher += `   ${booking.children} criança(s) meia\n`;
    }
    voucher += `\n💰 *Valor Total:* ${formatCurrency(booking.total_price)}\n`;
    voucher += `✅ *Status:* CONFIRMADO\n\n`;
    voucher += `⌚ *Horário:* ${d.horario_entrada} às ${d.horario_saida}\n\n`;
    voucher += `💦 *Incluso:*\n`;
    d.estrutura.forEach(item => {
      voucher += `${item}\n`;
    });
    voucher += `\n═══════════════════\n`;
    voucher += `_Apresente este voucher na recepção_\n`;
    voucher += `_Válido somente para ${dataFormatada}_\n`;
    voucher += `═══════════════════`;

    await MessageApi.create({
      conversationId: props.conversationId,
      message: voucher,
      private: false,
    });
    useAlert('Voucher enviado!');
  } catch (error) {
    useAlert('Erro ao enviar voucher');
  } finally {
    isSending.value = false;
  }
};

onMounted(() => {
  fetchBookings();
  fetchRooms();
  fetchMotorReservaQuoteData();
});
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('HOTEL_BOOKING.TITLE') }}
        </h3>
        <span
          v-if="bookings.length"
          class="text-xs text-n-slate-10 bg-n-slate-3 px-1.5 py-0.5 rounded"
        >
          {{ bookings.length }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-if="activeSection === 'form' || activeSection === 'dayuse'"
          :label="$t('HOTEL_BOOKING.FORM.CANCEL')"
          slate
          xs
          @click="activeSection = 'list'"
        />
        <template v-if="activeSection === 'list'">
          <Button
            label="Day-Use"
            xs
            faded
            @click="
              loadDayUseForEdit(null);
              createDayUse();
            "
          />
          <Button
            :label="$t('HOTEL_BOOKING.NEW_BOOKING')"
            xs
            @click="createBooking"
          />
        </template>
      </div>
    </div>

    <!-- Form Section (Hospedagem) -->
    <div v-if="activeSection === 'form'" class="flex-1 overflow-auto">
      <MotorReservaOrcamentoForm
        v-if="motorReservaEnabled"
        :booking="selectedBooking"
        :quote-data="motorReservaQuoteData"
        :contact="contact"
        :conversation-id="conversationId"
        @saved="onSaved"
        @cancel="activeSection = 'list'"
      />
      <HotelBookingForm
        v-else
        :booking="selectedBooking"
        :rooms="rooms"
        :contact-id="contactId"
        :conversation-id="conversationId"
        @saved="onSaved"
        @cancel="activeSection = 'list'"
      />
    </div>

    <!-- Day-Use Section -->
    <div v-else-if="activeSection === 'dayuse'" class="flex-1 overflow-auto">
      <div class="flex flex-col gap-4 p-4">
        <!-- Header -->
        <div
          class="flex items-center gap-2 p-3 bg-n-blue-2 rounded-lg border border-n-blue-7"
        >
          <span class="text-xl">🏊‍♂️</span>
          <div>
            <div class="text-sm font-semibold text-n-blue-11">Day-Use</div>
            <div class="text-xs text-n-blue-10">
              Pacote dia de lazer para o cliente
            </div>
          </div>
        </div>

        <!-- Data -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Data do Day-Use</label
          >
          <input
            v-model="dayUseDate"
            type="date"
            class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none"
          />
        </div>

        <!-- Pacote Selection -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-2"
            >Pacote</label
          >
          <div class="flex flex-col gap-2">
            <button
              v-for="pacote in dayUseConfig.pacotes"
              :key="pacote.id"
              type="button"
              class="flex items-center justify-between p-3 rounded-lg border-2 text-left transition-all"
              :class="
                selectedPacote === pacote.id
                  ? 'border-n-brand-9 bg-n-brand-2'
                  : 'border-n-weak bg-n-surface-1 hover:border-n-brand-7'
              "
              @click="selectedPacote = pacote.id"
            >
              <div>
                <div
                  class="text-sm font-medium"
                  :class="
                    selectedPacote === pacote.id
                      ? 'text-n-brand-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ pacote.id === 'completo' ? '☕🍽️' : '🍽️' }}
                  {{ pacote.nome }}
                </div>
              </div>
              <span class="text-sm font-bold text-n-brand-11">{{
                formatCurrency(pacote.preco)
              }}</span>
            </button>
          </div>
        </div>

        <!-- Pessoas -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1"
              >👨‍👩‍👧‍👦 Adultos</label
            >
            <input
              v-model.number="numAdultos"
              type="number"
              min="0"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              👦 Crianças ({{ dayUseConfig.criancas_meia_de }}-{{
                dayUseConfig.criancas_meia_ate
              }}
              anos)
            </label>
            <input
              v-model.number="numCriancasMeia"
              type="number"
              min="0"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none"
            />
          </div>
        </div>

        <div class="text-xs text-n-slate-10 bg-n-slate-2 rounded-lg px-3 py-2">
          👶 Crianças até {{ dayUseConfig.criancas_gratis_ate }} anos não pagam
        </div>

        <!-- Resumo -->
        <div class="bg-n-slate-2 rounded-lg p-3 text-xs">
          <div
            v-if="numAdultos > 0"
            class="flex justify-between text-n-slate-11"
          >
            <span
              >{{ numAdultos }} adulto(s) x
              {{ formatCurrency(currentPacote.preco) }}</span
            >
            <span>{{ formatCurrency(dayUseTotalAdultos) }}</span>
          </div>
          <div
            v-if="numCriancasMeia > 0"
            class="flex justify-between text-n-slate-11 mt-1"
          >
            <span
              >{{ numCriancasMeia }} criança(s) meia x
              {{ formatCurrency(currentPacote.preco / 2) }}</span
            >
            <span>{{ formatCurrency(dayUseTotalMeia) }}</span>
          </div>
          <div
            class="flex justify-between text-n-slate-12 font-semibold mt-2 pt-2 border-t border-n-weak"
          >
            <span>Total</span>
            <span class="text-base">{{ formatCurrency(dayUseTotal) }}</span>
          </div>
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Observações</label
          >
          <textarea
            v-model="dayUseNotes"
            rows="2"
            class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none"
            placeholder="Observações adicionais..."
          />
        </div>

        <!-- Config toggle -->
        <details class="bg-n-slate-2 rounded-lg border border-n-weak">
          <summary
            class="px-3 py-2 cursor-pointer text-xs font-medium text-n-slate-11 flex items-center gap-1"
          >
            <span class="i-lucide-settings size-3" />
            Configurar informações do Day-Use
          </summary>
          <div class="p-3 pt-1 space-y-3 border-t border-n-weak">
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Título</label>
              <input
                v-model="dayUseConfig.titulo"
                type="text"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
              />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="block text-xs text-n-slate-10 mb-1"
                  >Entrada</label
                >
                <input
                  v-model="dayUseConfig.horario_entrada"
                  type="text"
                  class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-10 mb-1">Saída</label>
                <input
                  v-model="dayUseConfig.horario_saida"
                  type="text"
                  class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1"
                >Nome do Hotel</label
              >
              <input
                v-model="dayUseConfig.nome_hotel"
                type="text"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
              />
            </div>
            <!-- Pacotes editáveis -->
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Pacotes</label>
              <div
                v-for="(pacote, idx) in dayUseConfig.pacotes"
                :key="idx"
                class="flex gap-2 mb-2"
              >
                <input
                  v-model="pacote.nome"
                  type="text"
                  class="flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-2 py-1.5 text-xs text-n-slate-12 focus:outline-none"
                />
                <div class="flex items-center gap-1">
                  <span class="text-xs text-n-slate-10">R$</span>
                  <input
                    v-model.number="pacote.preco"
                    type="number"
                    step="0.01"
                    class="w-20 rounded-lg border border-n-weak bg-n-surface-1 px-2 py-1.5 text-xs text-n-slate-12 focus:outline-none"
                  />
                </div>
              </div>
            </div>
            <!-- Formas de pagamento -->
            <div>
              <label class="block text-xs text-n-slate-10 mb-1"
                >Formas de Pagamento</label
              >
              <div class="space-y-1">
                <div
                  v-for="(forma, idx) in dayUseConfig.formas_pagamento"
                  :key="idx"
                  class="flex items-center gap-2"
                >
                  <input
                    v-model="forma.disponivel"
                    type="checkbox"
                    class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-9"
                  />
                  <span class="text-xs text-n-slate-11">{{ forma.tipo }}</span>
                </div>
                <div class="flex items-center gap-2 mt-1">
                  <input
                    v-model="dayUseConfig.pagamento_antecipado"
                    type="checkbox"
                    class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-9"
                  />
                  <span class="text-xs text-n-slate-11"
                    >Pagamento antecipado</span
                  >
                </div>
                <div class="flex items-center gap-2">
                  <input
                    v-model="dayUseConfig.pagamento_recepcao"
                    type="checkbox"
                    class="rounded border-n-weak text-n-brand-9 focus:ring-n-brand-9"
                  />
                  <span class="text-xs text-n-slate-11"
                    >Pagamento na recepção</span
                  >
                </div>
              </div>
            </div>
            <!-- Estrutura -->
            <div>
              <label class="block text-xs text-n-slate-10 mb-1"
                >Estrutura de Lazer</label
              >
              <textarea
                :value="dayUseConfig.estrutura.join('\n')"
                rows="6"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
                @blur="
                  dayUseConfig.estrutura = $event.target.value
                    .split('\n')
                    .filter(l => l.trim())
                "
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1"
                >Chamada Final</label
              >
              <input
                v-model="dayUseConfig.chamada_final"
                type="text"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-1.5 text-xs text-n-slate-12 focus:outline-none"
              />
            </div>
          </div>
        </details>

        <!-- Actions -->
        <div class="flex items-center gap-2 pt-2">
          <Button label="Cancelar" slate sm @click="activeSection = 'list'" />
          <div class="flex-1" />
          <Button
            label="Salvar Day-Use"
            sm
            :disabled="isSavingDayUse"
            :is-loading="isSavingDayUse"
            @click="saveDayUse"
          />
        </div>
      </div>
    </div>

    <!-- List Section -->
    <div v-else class="flex-1 overflow-auto">
      <!-- Filter tabs -->
      <div class="flex gap-1 px-4 py-2 border-b border-n-weak">
        <button
          v-for="filter in ['all', 'budgets', 'dayuse', 'reservations']"
          :key="filter"
          class="text-xs px-2.5 py-1 rounded-full transition-colors"
          :class="
            selectedFilter === filter
              ? 'bg-n-brand-2 text-n-brand-11 font-medium'
              : 'text-n-slate-11 hover:bg-n-slate-3'
          "
          @click="selectedFilter = filter"
        >
          {{
            filter === 'dayuse'
              ? '🏊 Day-Use'
              : $t(`HOTEL_BOOKING.FILTER.${filter.toUpperCase()}`)
          }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-8">
        <span class="text-sm text-n-slate-10">
          {{ $t('HOTEL_BOOKING.LOADING') }}
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!filteredBookings.length"
        class="flex flex-col items-center justify-center py-8 gap-3"
      >
        <span class="text-sm text-n-slate-10">
          {{ $t('HOTEL_BOOKING.NO_BOOKINGS') }}
        </span>
        <div class="flex gap-2">
          <Button
            label="Novo Day-Use"
            sm
            faded
            @click="
              loadDayUseForEdit(null);
              createDayUse();
            "
          />
          <Button
            :label="$t('HOTEL_BOOKING.NEW_BUDGET')"
            sm
            @click="createBooking"
          />
        </div>
      </div>

      <!-- Booking cards -->
      <div v-else class="flex flex-col gap-2 p-4">
        <div
          v-for="booking in filteredBookings"
          :key="booking.id"
          class="border border-n-weak rounded-lg p-3 bg-n-surface-1 hover:bg-n-surface-2 transition-colors"
        >
          <div class="flex items-start justify-between mb-2">
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  booking.booking_type === 'day_use'
                    ? '🏊 Day-Use'
                    : booking.motor_reserva_room_name ||
                      booking.hotel_room?.name ||
                      'Quarto'
                }}
              </span>
              <span
                class="ml-2 text-xs px-1.5 py-0.5 rounded"
                :class="statusColor(booking.status)"
              >
                {{ $t(`HOTEL_BOOKING.STATUS.${booking.status.toUpperCase()}`) }}
              </span>
              <span
                v-if="booking.booking_type === 'day_use'"
                class="ml-1 text-xs px-1.5 py-0.5 rounded bg-n-blue-2 text-n-blue-11"
              >
                Day-Use
              </span>
              <span
                v-if="booking.is_holiday"
                class="ml-1 text-xs px-1.5 py-0.5 rounded bg-n-amber-2 text-n-amber-11"
              >
                Feriado
              </span>
            </div>
            <span class="text-sm font-semibold text-n-brand-11">
              {{ formatCurrency(booking.total_price) }}
            </span>
          </div>
          <div
            v-if="booking.motor_reserva_rate_name"
            class="text-xs text-n-slate-10 mb-2"
          >
            Tarifa: {{ booking.motor_reserva_rate_name }}
          </div>

          <div class="grid grid-cols-3 gap-2 text-xs text-n-slate-11 mb-2">
            <template v-if="booking.booking_type === 'day_use'">
              <div>
                <span class="text-n-slate-10">Data:</span>
                {{ formatDate(booking.day_use_date || booking.check_in) }}
              </div>
              <div>
                <span class="text-n-slate-10">Adultos:</span>
                {{ booking.adults }}
              </div>
              <div v-if="booking.children > 0">
                <span class="text-n-slate-10">Crianças:</span>
                {{ booking.children }}
              </div>
            </template>
            <template v-else>
              <div>
                <span class="text-n-slate-10">Check-in:</span>
                {{ formatDate(booking.check_in) }}
              </div>
              <div>
                <span class="text-n-slate-10">Check-out:</span>
                {{ formatDate(booking.check_out) }}
              </div>
              <div>
                <span class="text-n-slate-10"
                  >{{ $t('HOTEL_BOOKING.SUMMARY.NIGHTS') }}:</span
                >
                {{ booking.nights }}
              </div>
            </template>
          </div>

          <div
            v-if="booking.booking_type !== 'day_use'"
            class="text-xs text-n-slate-11 mb-2 flex gap-3 flex-wrap"
          >
            <span>
              <span class="text-n-slate-10">Adultos:</span>
              {{ booking.adults || booking.guests }}
            </span>
            <span v-if="booking.children > 0">
              <span class="text-n-slate-10">Crianças F1:</span>
              {{ booking.children }}
            </span>
            <span v-if="booking.children2 > 0">
              <span class="text-n-slate-10">Crianças F2:</span>
              {{ booking.children2 }}
            </span>
          </div>

          <div
            v-if="booking.notes"
            class="text-xs text-n-slate-10 mb-2 truncate"
          >
            {{ booking.notes }}
          </div>

          <!-- Actions -->
          <div
            class="flex items-center gap-1 pt-2 border-t border-n-weak flex-wrap"
          >
            <!-- Day-Use specific actions -->
            <template v-if="booking.booking_type === 'day_use'">
              <Button
                v-if="booking.status === 'budget'"
                label="Enviar Proposta"
                xs
                :disabled="isSending"
                @click="sendDayUseMessage(booking)"
              />
              <Button
                v-if="booking.status === 'budget'"
                label="Confirmar"
                xs
                slate
                @click="updateStatus(booking, 'confirmed')"
              />
              <Button
                v-if="
                  booking.status === 'confirmed' ||
                  booking.status === 'checked_in'
                "
                label="Enviar Voucher"
                xs
                faded
                :disabled="isSending"
                @click="sendDayUseVoucher(booking)"
              />
              <Button
                v-if="booking.status === 'confirmed'"
                label="Check-in"
                xs
                slate
                @click="updateStatus(booking, 'checked_in')"
              />
              <Button
                v-if="booking.status === 'checked_in'"
                label="Check-out"
                xs
                slate
                @click="updateStatus(booking, 'checked_out')"
              />
            </template>

            <!-- Regular booking actions -->
            <template v-else>
              <Button
                v-if="booking.status === 'budget'"
                :label="$t('HOTEL_BOOKING.SEND_BUDGET')"
                xs
                @click="sendBudgetMessage(booking)"
              />
              <Button
                v-if="booking.status === 'budget'"
                label="Confirmar"
                xs
                slate
                @click="
                  motorReservaEnabled
                    ? openConfirmForm(booking)
                    : updateStatus(booking, 'confirmed')
                "
              />
              <Button
                v-if="booking.status === 'confirmed'"
                label="Check-in"
                xs
                slate
                @click="updateStatus(booking, 'checked_in')"
              />
              <Button
                v-if="booking.status === 'checked_in'"
                label="Check-out"
                xs
                slate
                @click="updateStatus(booking, 'checked_out')"
              />
            </template>

            <div class="flex-1" />
            <Button
              v-if="booking.status === 'budget'"
              label="Editar"
              xs
              slate
              @click="editBooking(booking)"
            />
            <Button
              v-if="
                booking.status === 'budget' || booking.status === 'confirmed'
              "
              label="Cancelar"
              xs
              slate
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="updateStatus(booking, 'cancelled')"
            />
            <Button
              v-if="booking.status === 'budget'"
              label="Excluir"
              xs
              slate
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="deleteBooking(booking)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Form Modal -->
    <div
      v-if="showConfirmForm && confirmingBooking"
      class="absolute inset-0 z-50 bg-n-background overflow-y-auto"
    >
      <MotorReservaConfirmForm
        :booking="confirmingBooking"
        :contact="contact"
        :conversation-id="conversationId"
        :quote-data="motorReservaQuoteData"
        @confirmed="onBookingConfirmed"
        @cancel="showConfirmForm = false"
      />
    </div>
  </div>
</template>
